import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { PasswordValidationComponent } from '../../components/password-validation/password-validation.component';
import { InputComponent } from '../../components/input/input.component';
import { AuthorizeService } from '../../services/authorize.service';
import { AlertService } from '../../services/alert.service';
import { setContainerTypeAction, showLoadingMaskAction } from '../../ngrx/actions/global.actions';

/**
 * Landing page for the emailed reset link
 * (/authentication/reset-password?email=...&token=...). Sets a new password
 * against the single-use token.
 */
@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, InputComponent],
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss']
})
export class ResetPasswordComponent extends PasswordValidationComponent implements OnInit {

  resetForm: FormGroup = new FormGroup({
    password: new FormControl('', [
      Validators.required,
      Validators.minLength(8),
      this.passwordValidation.bind(this)
    ]),
    confirmPassword: new FormControl('', [
      Validators.required,
      this.passwordMatchValidation.bind(this)
    ])
  });

  email = '';
  token = '';
  linkInvalid = false;
  isSubmitting = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authorizeService: AuthorizeService,
    private alertService: AlertService
  ) {
    super();
  }

  ngOnInit(): void {
    this.dispatch(setContainerTypeAction({ containerType: 'normal' }));
    this.subs.push(
      this.route.queryParams.subscribe(params => {
        this.email = params['email'] || '';
        this.token = params['token'] || '';
        this.linkInvalid = !this.email || !this.token;
      })
    );
  }

  passwordMatchValidation(passwordMatchControl: FormControl): ValidationErrors | null {
    if (passwordMatchControl.value === this.resetForm?.get('password')?.value) {
      return null;
    }
    return { passwordMatch: 'Passwords do not match!' };
  }

  onSubmit(): void {
    if (!this.resetForm.valid) {
      for (const control in this.resetForm.controls) {
        if (!this.resetForm.get(control)?.valid) {
          this.resetForm.get(control)?.markAsDirty();
          this.alertService.showSnackBar(`Error with ${control} input`);
          return;
        }
      }
      return;
    }

    this.isSubmitting = true;
    this.dispatch(showLoadingMaskAction({ showLoadingMask: true }));
    this.subs.push(
      this.authorizeService.resetPassword(
        this.email,
        this.token,
        this.resetForm.get('password')?.value
      ).subscribe({
        next: (result) => {
          this.isSubmitting = false;
          this.dispatch(showLoadingMaskAction({ showLoadingMask: false }));
          this.alertService.showSnackBar(result.message);
          if (result.success) {
            this.router.navigate(['/authentication/login']);
          }
        },
        error: (error) => {
          console.error('[ResetPassword] Request failed:', error);
          this.isSubmitting = false;
          this.dispatch(showLoadingMaskAction({ showLoadingMask: false }));
          this.alertService.showSnackBar('Unable to reset password. Please try again.');
        }
      })
    );
  }
}
